import { STAFF_SCHEDULE_DAYS } from "./staffScheduleConstants";

/** Parse "HH:mm" (24h) to { hour12, minute, period } */
export function parseTime24(hhmm) {
  if (!hhmm || typeof hhmm !== "string") {
    return { hour12: "8", minute: "00", period: "AM" };
  }
  const [hStr, mStr] = hhmm.split(":");
  let h = Number.parseInt(hStr, 10);
  const minute = mStr?.padStart(2, "0") || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return { hour12: String(h), minute, period };
}

/** Build "HH:mm" 24h from 12h parts */
export function buildTime24(hour12, minute, period) {
  let h = Number.parseInt(String(hour12), 10);
  const m = String(minute || "00").padStart(2, "0");
  if (period === "AM") {
    if (h === 12) h = 0;
  } else if (h !== 12) {
    h += 12;
  }
  return `${String(h).padStart(2, "0")}:${m}`;
}

/** Display "08:00 AM - 04:00 PM" */
export function formatTimeRange12(startTime, endTime) {
  return `${formatTime12(startTime)} - ${formatTime12(endTime)}`;
}

export function formatTime12(hhmm) {
  const { hour12, minute, period } = parseTime24(hhmm);
  return `${hour12}:${minute} ${period}`;
}

export function timeToMinutes(hhmm) {
  const [h, m] = String(hhmm || "00:00").split(":").map(Number);
  return h * 60 + m;
}

export function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

/** Client-side overlap check for immediate feedback */
export function findRoleOverlapError({ candidate, rows, excludeSlotId }) {
  const cStart = timeToMinutes(candidate.startTime);
  const cEnd = timeToMinutes(candidate.endTime);

  for (const row of rows) {
    if (row.role !== candidate.role) continue;
    for (const slot of row.slots || []) {
      if (excludeSlotId != null && slot.id === excludeSlotId) continue;
      if (slot.dayOfWeek !== candidate.dayOfWeek) continue;
      if (slot.employeeId === candidate.employeeId) continue;
      const rStart = timeToMinutes(slot.startTime);
      const rEnd = timeToMinutes(slot.endTime);
      if (rangesOverlap(cStart, cEnd, rStart, rEnd)) {
        const dayLabel =
          STAFF_SCHEDULE_DAYS.find((d) => d.value === candidate.dayOfWeek)
            ?.label || "this day";
        return `This time overlaps with another ${candidate.role} staff member on ${dayLabel}.`;
      }
    }
  }
  return null;
}

export function validateShiftTimes({
  startTime,
  endTime,
  breakStartTime,
  breakEndTime,
  hasBreak,
}) {
  const startMin = timeToMinutes(startTime);
  const endMin = timeToMinutes(endTime);
  if (startMin >= endMin) {
    return "End time must be after start time";
  }
  if (hasBreak) {
    if (!breakStartTime || !breakEndTime) {
      return "Both break start and end are required";
    }
    const bStart = timeToMinutes(breakStartTime);
    const bEnd = timeToMinutes(breakEndTime);
    if (bStart >= bEnd) return "Break end must be after break start";
    if (bStart < startMin || bEnd > endMin) {
      return "Break must fall within the scheduled shift";
    }
  }
  return null;
}

export function slotForDay(slots, dayOfWeek) {
  return (slots || []).find((s) => s.dayOfWeek === dayOfWeek) || null;
}
